import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { formatBRL } from "@/lib/format";

import { BadgeSeguradora } from "./comuns";
import { temFuncao } from "./funcoes";
import {
  ConfirmarAcao,
  DialogoEndosso,
  DialogoLead,
  DialogoLinkCartao,
  DialogoNovoLead,
  DialogoPagamento,
  DialogoVencimento,
} from "./PortalAzosDialogos";
import { dataCurta } from "./portalAzos";
import {
  acoesDaProposta,
  type ApolicePortal,
  type ContrapropostaPortal,
  FUNCAO_DA_ACAO,
  type LeadPortal,
  lerHistorico,
  type PropostaPortal,
  ROTULOS_HISTORICO,
  rotuloMetodo,
  rotuloProposta,
  type TipoAcao,
  usePortalAzos,
} from "./portalAzosAcoes";

/**
 * Ações que gravam no portal da Azos, nas telas por função (Propostas,
 * Apólices, Exigências, página Azos). Cada botão aparece pela função que a
 * seguradora declara (funcoes.ts) e pela situação do item; toda ação passa
 * pela confirmação de PortalAzosDialogos e só age na própria conexão.
 */

export const ROTULO_ACAO_PROPOSTA: Partial<Record<TipoAcao, string>> = {
  proposta_assinatura: "Enviar para assinatura",
  proposta_reenviar_assinatura: "Reenviar assinatura",
  proposta_link_cartao: "Link de cartão",
  proposta_pagamento: "Pix ou boleto",
  proposta_vencimento: "Alterar vencimento",
  proposta_duplicar: "Duplicar",
  proposta_arquivar: "Arquivar",
};

export type AcaoAberta =
  | { tipo: "simples"; acao: TipoAcao; dados: Record<string, unknown>; descricao: string }
  | { tipo: "pagamento"; acao: "proposta_pagamento" | "apolice_pagamento"; dados: Record<string, unknown>; descricao: string }
  | { tipo: "vencimento" | "cartao"; propostaId: string; descricao: string }
  | { tipo: "endosso"; apoliceId: string; descricao: string }
  | { tipo: "novo-lead" }
  | { tipo: "lead"; modo: "situacao" | "nota"; lead: LeadPortal };

const podeNaAzos = (acao: TipoAcao) => temFuncao("azos", FUNCAO_DA_ACAO[acao]);

/** Ações da proposta na situação dela, só as de função que a Azos declara. */
export const acoesPermitidasDaProposta = (p: Pick<PropostaPortal, "situacao">) =>
  acoesDaProposta(p.situacao).filter(podeNaAzos);

export const abrirAcaoProposta = (p: PropostaPortal, acao: TipoAcao): AcaoAberta => {
  const descricao = `Proposta de ${p.segurado ?? "segurado sem nome"} · ${rotuloProposta(p)}.`;
  if (acao === "proposta_pagamento") return { tipo: "pagamento", acao, dados: { proposta_id: p.id }, descricao };
  if (acao === "proposta_vencimento") return { tipo: "vencimento", propostaId: p.id, descricao };
  if (acao === "proposta_link_cartao") return { tipo: "cartao", propostaId: p.id, descricao };
  return { tipo: "simples", acao, dados: { proposta_id: p.id }, descricao };
};

/** Alterar cobertura e Pix ou boleto da apólice. */
export const acoesDaApolice = (a: ApolicePortal): { rotulo: string; aberta: AcaoAberta }[] => {
  const descricao = `Apólice ${a.numero ?? a.id} · ${a.segurado ?? "segurado sem nome"}.`;
  const itens: { rotulo: string; aberta: AcaoAberta }[] = [];
  if (podeNaAzos("endosso")) itens.push({ rotulo: "Alterar cobertura", aberta: { tipo: "endosso", apoliceId: a.id, descricao } });
  if (a.proposta_id && podeNaAzos("apolice_pagamento")) {
    itens.push({
      rotulo: "Pix ou boleto",
      aberta: { tipo: "pagamento", acao: "apolice_pagamento", dados: { apolice_id: a.id, proposta_id: a.proposta_id }, descricao },
    });
  }
  return itens;
};

export const abrirAcaoContraproposta = (c: ContrapropostaPortal, acao: "contraproposta_aceitar" | "contraproposta_recusar"): AcaoAberta => ({
  tipo: "simples",
  acao,
  dados: { contraproposta_id: c.id },
  descricao: `Contraproposta da Azos para ${c.segurado ?? "segurado sem nome"}.`,
});

/** Os diálogos de confirmação; `aoConcluir` roda depois da gravação. */
export const DialogosAzos = ({
  aberta,
  aoFechar,
  aoConcluir,
}: {
  aberta: AcaoAberta | null;
  aoFechar: () => void;
  aoConcluir?: () => void;
}) => {
  const qc = useQueryClient();
  const concluido = () => {
    void qc.invalidateQueries({ queryKey: ["portal-azos"] });
    void qc.invalidateQueries({ queryKey: ["azos-conexoes"] });
    aoConcluir?.();
  };
  if (!aberta) return null;
  switch (aberta.tipo) {
    case "simples":
      return (
        <ConfirmarAcao
          tipo={aberta.acao}
          aberto
          aoFechar={aoFechar}
          aoConcluir={concluido}
          descricao={aberta.descricao}
          montar={() => aberta.dados}
        />
      );
    case "pagamento":
      return (
        <DialogoPagamento tipo={aberta.acao} dadosBase={aberta.dados} descricao={aberta.descricao} aberto aoFechar={aoFechar} aoConcluir={concluido} />
      );
    case "vencimento":
      return <DialogoVencimento propostaId={aberta.propostaId} descricao={aberta.descricao} aberto aoFechar={aoFechar} aoConcluir={concluido} />;
    case "cartao":
      return <DialogoLinkCartao propostaId={aberta.propostaId} descricao={aberta.descricao} aberto aoFechar={aoFechar} />;
    case "endosso":
      return <DialogoEndosso apoliceId={aberta.apoliceId} descricao={aberta.descricao} aberto aoFechar={aoFechar} />;
    case "novo-lead":
      return <DialogoNovoLead aberto aoFechar={aoFechar} aoConcluir={concluido} />;
    case "lead":
      return (
        <DialogoLead
          modo={aberta.modo}
          leadId={aberta.lead.id}
          nome={aberta.lead.nome ?? "sem nome"}
          situacaoAtual={aberta.lead.situacao}
          aberto
          aoFechar={aoFechar}
          aoConcluir={concluido}
        />
      );
  }
};

/** Histórico das ações gravadas num item (proposta, apólice...). */
const HistoricoDoItem = ({ crmUserId, alvo }: { crmUserId: number; alvo: string }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["portal-azos", "historico", crmUserId],
    queryFn: () => lerHistorico(crmUserId),
  });
  if (isLoading) return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  if (error) return <p className="text-sm text-destructive">{(error as Error).message}</p>;
  const doItem = (data ?? []).filter((h) => h.alvo === alvo);
  if (doItem.length === 0) return <p className="text-sm text-muted-foreground">Nenhuma ação registrada.</p>;
  return (
    <ol className="divide-y">
      {doItem.map((h) => (
        <li key={h.id} className="flex items-start justify-between gap-3 py-2 text-sm">
          <div>
            {ROTULOS_HISTORICO[h.tipo] ?? h.tipo}
            <p className={h.ok ? "text-xs text-muted-foreground" : "text-xs text-destructive"}>
              {h.ok ? "Concluída" : (h.erro ?? `Recusada (HTTP ${h.status_http ?? "—"})`)}
            </p>
          </div>
          <span className="shrink-0 text-xs text-muted-foreground">{new Date(h.criado_em).toLocaleString("pt-BR")}</span>
        </li>
      ))}
    </ol>
  );
};

/** Detalhe da proposta da Azos (lida na hora do portal) com as ações dela. */
export const PropostaAzosDetalhe = ({
  proposta,
  crmUserId,
  aoFechar,
}: {
  proposta: PropostaPortal | null;
  crmUserId: number;
  aoFechar: () => void;
}) => {
  const [aberta, setAberta] = useState<AcaoAberta | null>(null);
  const acoes = proposta ? acoesPermitidasDaProposta(proposta) : [];
  return (
    <>
      <Sheet open={!!proposta && !aberta} onOpenChange={(v) => !v && !aberta && aoFechar()}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
          {proposta && (
            <>
              <SheetHeader>
                <SheetTitle>{proposta.segurado ?? "Proposta"}</SheetTitle>
                <SheetDescription>
                  <BadgeSeguradora codigo="azos" /> · {rotuloProposta(proposta)} · {dataCurta(proposta.criada_em)}
                </SheetDescription>
              </SheetHeader>
              <section className="flex flex-col gap-1 px-4 text-sm">
                <span>
                  Prêmio {proposta.premio != null ? formatBRL(proposta.premio) : "—"} · {rotuloMetodo(proposta.metodo)} ·{" "}
                  {proposta.frequencia === "annual" ? "anual" : "mensal"}
                </span>
                {proposta.motivo_recusa && <span className="text-muted-foreground">{proposta.motivo_recusa}</span>}
              </section>
              {acoes.length > 0 && (
                <section className="flex flex-col gap-2 px-4">
                  <h3 className="text-sm font-semibold">Ações na seguradora</h3>
                  <div className="flex flex-wrap gap-2">
                    {acoes.map((a) => (
                      <Button key={a} type="button" size="sm" variant="outline" onClick={() => setAberta(abrirAcaoProposta(proposta, a))}>
                        {ROTULO_ACAO_PROPOSTA[a] ?? a}
                      </Button>
                    ))}
                  </div>
                </section>
              )}
              <section className="flex flex-col gap-2 px-4 pb-6">
                <h3 className="text-sm font-semibold">Histórico</h3>
                <HistoricoDoItem crmUserId={crmUserId} alvo={proposta.id} />
              </section>
            </>
          )}
        </SheetContent>
      </Sheet>
      <DialogosAzos aberta={aberta} aoFechar={() => setAberta(null)} />
    </>
  );
};

/**
 * Ações da apólice da Azos na própria conexão (Apólices › Faturas). O id do
 * portal sai da lista lida na hora, casando pelo número da apólice; apólice de
 * outra corretora não aparece nela e fica sem botões.
 */
export const AcoesApoliceAzos = ({ numero }: { numero: string }) => {
  const [aberta, setAberta] = useState<AcaoAberta | null>(null);
  const { data, isLoading, error } = usePortalAzos<ApolicePortal>("apolices", true);
  if (isLoading) return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  if (error) return <p className="text-xs text-destructive">{(error as Error).message}</p>;
  const apolice = (data ?? []).find((a) => a.numero === numero);
  const itens = apolice ? acoesDaApolice(apolice) : [];
  if (itens.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-muted-foreground">Ações na seguradora</span>
      {itens.map((i) => (
        <Button key={i.rotulo} type="button" size="sm" variant="outline" onClick={() => setAberta(i.aberta)}>
          {i.rotulo}
        </Button>
      ))}
      <DialogosAzos aberta={aberta} aoFechar={() => setAberta(null)} />
    </div>
  );
};
